import { Box, Button, Grid } from "@mui/material";
import { addDays, format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import { getRouteProgressDataThunk } from "../../app/features/dedicatedDelivery/routeSlice";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import Loader from "../../components/common/loader/Loader";
import { ImportRouteProgress } from "./ImportRouteProgress";
import RouteProgressData from "./RouteProgressData";
import { RouteProgressOverview } from "./RouteProgressOverview";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../components/common/filter/GridDataFilter";
import {
  combinedFilteredData,
  fdpRouteProgressFilter,
} from "./RouteProgressTypes";
import * as XLSX from "xlsx";
import {
  DdFdpRouteProgressDto,
  DdRouteProgressDto,
} from "../../app/models/responseModels/DdRouteProgressResponse";
import { RouteProgressContext } from "./useRouteProgresContext";
import agent from "../../app/api/agent";
import { MRT_Row } from "material-react-table";

const RouteProgressDedicated = () => {
  const navigate = useNavigate();
  const location = useLocation().search;
  const [resetFilter, setResetFilter] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);

  const clientId = new URLSearchParams(location).get("client_id");

  // keep startDate in state
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject().setDate(addDays(new Date(), 0)),
    new DateObject().setDate(new Date()),
  ]);

  const dispatch = useAppDispatch();

  // fetch routeData from store
  const { isLoading, routeProgressData } = useSelector(
    (state: RootState) => state.dedicatedDeliveryFetchRoute
  );

  const [clonedRouteProgressData, setClonedRouteProgressData] = useState<
    DdRouteProgressDto[]
  >(routeProgressData?.routeProgress);

  const [clonedFdpRouteProgressData, setClonedFdpRouteProgressData] = useState<
    DdFdpRouteProgressDto[]
  >(routeProgressData?.fdpRouteProgress);

  useEffect(() => {
    console.log(clonedRouteProgressData);

    getRouteProgressData();
  }, []);
  

  useEffect(() => {
    setClonedFdpRouteProgressData(routeProgressData?.fdpRouteProgress);
    setClonedRouteProgressData(routeProgressData?.routeProgress);
    console.log(`Route Progress List for Target:`, clonedFdpRouteProgressData);
  }, [routeProgressData]);

  const getRouteProgressData = useCallback(async () => {
    try {
      const response = await dispatch(
        getRouteProgressDataThunk({
          clientId: Number(clientId),
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD"),
        })
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "clientData",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientDataException",
      });
    } finally {
    }
  }, [dateRange]);
  /**
   * This method is to handle export from UI
   * @author Mohit Kumar Bhadani
   */
  const handleExportRouteProgressData = () => {
    let formatStartDateP = dateRange[0].format("YYYY-MM-DD");
    let formatEndDateP = dateRange[1].format("YYYY-MM-DD");
    let rows =
      clonedRouteProgressData.length !== 0
        ? clonedRouteProgressData
        : routeProgressData?.routeProgress;

    // Define custom column headers
    const columnNames = [
      "Driver Name",
      "Route Status",
      "Current Progress",
      "Associated FDP Name",
      "Route ID",
      "Total Package(s)",
      "Returned Orders",
      "Orders - Undelivered - No Attempt",
      "Total RTS Package(s)",
      "Package(s) in Last 1 Hour",
      "Loadout Start time",
      "Driver Arrival Time",
      "Route Start Time",
      "Route Completion",
      "Last Delivery At",
      "Last Updated At",
      "RTS Summary",
      "Total Trip Times",
      "Estimated Trip Times",
    ];

    // Map rows data to match the custom header format
    const dataWithHeaders = rows.map((row) => {
      return {
        "Driver Name": row?.driverName,
        "Route Status": row?.ROUTE_STATUS,
        "Current Progress": row?.PROGRESS,
        "Associated FDP Name": row?.associatedFdpName,
        "Route ID": row?.ROUTE_ID,
        "Total Package(s)": row?.CURRENT_ORDERS_ON_BUNDLE,
        "Returned Orders": row?.RETURNED_ORDERS,
        "Orders - Undelivered - No Attempt": row?.ordersUndeliveredUnattempted,
        "Total RTS Package(s)": row?.totalRtsPackages,
        "Package(s) in Last 1 Hour": row?.stopsInLastOneHour,
        "Loadout Start time": row?.ARRIVAL_WINDOW,
        "Driver Arrival Time": row?.ARRIVED_AT,
        "Route Start Time": row?.STARTED_AT,
        "Route Completion": row?.COMPLETED_AT,
        "Last Delivery At": row?.LAST_DELIVERY_AT,
        "Last Updated At": row?.fileLastUpdateAt,
        "RTS Summary": row?.rtsPackageSummaryDetails?.rtsDetails
          ?.map((detail) => `${detail?.reason} (${detail?.totalPackages})`)
          .join(","),
        "Total Trip Times": row?.totalTripTimes,
        "Estimated Trip Times": row?.estimatedTripTime,

      };
    });
    // Prepend column names as the first row
    const worksheetData = [
      columnNames, // Add custom header names as the first row
      ...dataWithHeaders.map(Object.values), // Add actual data below the headers
    ];
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dates");
    XLSX.writeFile(
      workbook,
      "Route-Progress-Dedicated-" +
        formatStartDateP +
        "-to-" +
        formatEndDateP +
        ".xlsx",
      { compression: true }
    );
  };

  /**
   * This method is to handle export from UI
   * @author Mohit Kumar Bhadani
   */
  const handleExportFdpRouteProgressData = () => {
    let formatStartDateP = dateRange[0].format("YYYY-MM-DD");
    let formatEndDateP = dateRange[1].format("YYYY-MM-DD");
    let rows =
      clonedFdpRouteProgressData.length !== 0
        ? clonedFdpRouteProgressData
        : routeProgressData?.fdpRouteProgress;

    // Define custom column headers
    const columnNames = [
      "FDP Name",
      "Total Route(s)",
      "Total Package(s)",
      "Route Progress",
    ];

    // Map rows data to match the custom header format
    const dataWithHeaders = rows.map((row) => {
      return {
        "FDP Name": row.fdpName,
        "Total Route(s)": row?.totalRoutes,
        "Total Package(s)": row?.totalPackages,
        "Route Progress": row?.totalDeliveredPackages,
      };
    });
    console.log(rows);
    // Prepend column names as the first row
    const worksheetData = [
      columnNames, // Add custom header names as the first row
      ...dataWithHeaders.map(Object.values), // Add actual data below the headers
    ];
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Dates");
    XLSX.writeFile(
      workbook,
      "FDP-Route-Progress-Dedicated-" +
        formatStartDateP +
        "-to-" +
        formatEndDateP +
        ".xlsx",
      { compression: true }
    );
  };
  if (!clientId) {
    navigate("/route-progress-dedicated");
  }
  return (
    <Grid
      container
      paddingTop={2}
      rowSpacing={2}
      justifyContent="space-between"
    >
      <RouteProgressContext.Provider value={dateRange}>
        <Loader isLoading={exporting} />
        <Grid item>
          <Grid container item alignItems="center">
            <Box zIndex={100}>
              <DatePicker
                format="MM/DD/YYYY"
                className="green"
                dateSeparator=" ~ "
                range
                rangeHover
                maxDate={new Date()}
                value={dateRange}
                onChange={(dateObjects: DateObject[]) => {
                  setDateRange(dateObjects);
                }}
              />
            </Box>
            <Button size="large" onClick={getRouteProgressData}>
              SUBMIT
            </Button>
          </Grid>
        </Grid>

        <Grid item>
          <ImportRouteProgress clientId={Number(clientId)} />
        </Grid>
        <Grid item xs={12}>
          <Grid
            container
            paddingTop={2}
            rowSpacing={2}
            justifyContent="space-between"
          >
            {/* <Grid item xs={12}>
              <RouteProgressDedicatedOverviewFilter />
            </Grid> */}
            <Grid item xs={12}>
              <GridDataFilter
                filterableColumns={combinedFilteredData}
                title="Search  FDP Route Progress Data"
                dateRange={dateRange}
                resetFlag={resetFilter}
                asyncSubmitFunction={async (
                  filterState: FilterableColumnsState
                ) => {
                  if (Object.keys(filterState).length) {
                    const filteredFdpData = clonedFdpRouteProgressData?.filter(
                      (routeData: Record<string, any>) => {
                        for (const key in filterState) {
                          if (filterState.hasOwnProperty(key)) {
                            const filterValues = filterState[key].values;
                            if (key === "ARRIVAL_WINDOW") {
                              if (filterValues.length === 2) {
                                const dateField = new Date(routeData[key]);
                                const startDate = new Date(filterValues[0]);
                                const endDate = new Date(filterValues[1]);

                                endDate.setHours(23, 59, 59, 999);

                                const isDateInRange =
                                  dateField >= startDate &&
                                  dateField <= endDate;
                                if (!isDateInRange) {
                                  return false;
                                }
                              }
                            } else {
                              if (
                                filterValues.length > 0 &&
                                !filterValues.includes(routeData[key])
                              ) {
                                return false;
                              }
                            }
                          }
                        }
                        return true;
                      }
                    );
                    const filteredRouteData = clonedRouteProgressData?.filter(
                      (routeData: Record<string, any>) => {
                        for (const key in filterState) {
                          if (filterState.hasOwnProperty(key)) {
                            const filterValues = filterState[key].values;
                            if (
                              key === "ARRIVAL_WINDOW" ||
                              key === "STARTED_AT" ||
                              key === "COMPLETED_AT" ||
                              key === "LAST_DELIVERY_AT" ||
                              key === "fileLastUpdateAt"
                            ) {
                              if (filterValues.length === 2) {
                                const dateField = new Date(routeData[key]);
                                const startDate = new Date(filterValues[0]);
                                const endDate = new Date(filterValues[1]);
                                endDate.setHours(23, 59, 59, 999);
                                const isDateInRange =
                                  dateField >= startDate &&
                                  dateField <= endDate;
                                if (!isDateInRange) {
                                  return false;
                                }
                              }
                            } else {
                              if (
                                filterValues.length > 0 &&
                                !filterValues.includes(routeData[key])
                              ) {
                                return false;
                              }
                            }
                          }
                        }
                        return true;
                      }
                    );

                    console.log(
                      filterState,
                      routeProgressData?.fdpRouteProgress,
                      filteredFdpData,
                      filteredRouteData
                    );
                    setClonedFdpRouteProgressData(filteredFdpData);
                    setClonedRouteProgressData(filteredRouteData);
                  } else {
                    setClonedFdpRouteProgressData(
                      routeProgressData?.fdpRouteProgress
                    );
                    setClonedRouteProgressData(
                      routeProgressData?.routeProgress
                    );
                  }
                }}
                asyncSelectedColumnValues={(
                  columnName: string,
                  setClFilterableColumns: React.Dispatch<
                    React.SetStateAction<FilterableColumns>
                  >
                ) => {
                  const filteredFdpData = clonedFdpRouteProgressData
                    .map((e: Record<string, any>) => e[columnName])
                    .filter((value) => value !== undefined && value !== null);
                  const routeFilteredData = clonedRouteProgressData
                    .map((e: Record<string, any>) => e[columnName])
                    .filter((value) => value !== undefined && value !== null);

                  const combinedFilteredData = [
                    ...filteredFdpData,
                    ...routeFilteredData,
                  ];
                  setClFilterableColumns((prevState: FilterableColumns) => {
                    return {
                      ...prevState,
                      [columnName]: {
                        ...prevState[columnName],
                        options:
                          Array.from(new Set(combinedFilteredData)) ?? [],
                      },
                    };
                  });
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <RouteProgressOverview
                data={clonedFdpRouteProgressData}
                loading={isLoading}
                refresh={getRouteProgressData}
                handelExport={handleExportFdpRouteProgressData}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <RouteProgressDedicatedFilter />
            </Grid> */}

            {/* <Grid item xs={12}>
              <RouteProgressDedicatedFilter />
            </Grid> */}

            <Grid item xs={12}>
              <RouteProgressData
                data={clonedRouteProgressData}
                loading={isLoading}
                refresh={getRouteProgressData}
                handelExport={handleExportRouteProgressData}
              />
            </Grid>
          </Grid>
        </Grid>
      </RouteProgressContext.Provider>
    </Grid>
  );
};

export default RouteProgressDedicated;
